/**
 * Yuvarlama fonksiyonu: 2 ondalık basamağa yuvarla
 * Örnek: 2.15555 -> 2.16, 5.3565 -> 5.36
 */
export const roundToTwoDecimals = (value) => {
  return Math.round(value * 100) / 100;
};

/**
 * Format fonksiyonu: Değerin sonuna ₺ ekler
 * - Eğer değer 0 ise sadece "0 ₺" gösterir (0.00 değil)
 * - Eğer tam sayı ise ondalık kısmı göstermez (örn: 40 -> "40 ₺")
 * - Eğer ondalıklı ise 2 basamak gösterir (örn: 3.13 -> "3.13 ₺")
 */
export const formatCurrency = (input) => {
  if (input === null || input === undefined || input === "") return "";
  // Sayı da gelebildiği için string'e normalize et (endsWith/replace güvenliği)
  const value = String(input);
  // Zaten ₺ varsa tekrar ekleme
  if (value.endsWith("₺")) {
    // Ama içindeki sayıyı kontrol et
    const numStr = value.replace("₺", "").trim();
    const numValue = parseFloat(numStr);
    if (!isNaN(numValue) && numValue === 0) return "0 ₺";
    if (!isNaN(numValue) && numValue % 1 === 0) return `${numValue.toFixed(0)} ₺`;
    return value;
  }
  
  // Sayıyı parse et
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return `${value} ₺`;
  
  // Eğer değer 0 ise sadece "0 ₺" göster (0.00 değil)
  if (numValue === 0) return "0 ₺";
  
  // Yuvarlama: 2 ondalık basamağa yuvarla
  const roundedValue = roundToTwoDecimals(numValue);
  
  // Eğer tam sayı ise ondalık kısmı gösterme
  if (roundedValue % 1 === 0) {
    return `${roundedValue.toFixed(0)} ₺`;
  }
  
  // Ondalıklı ise 2 basamak göster
  return `${roundedValue.toFixed(2)} ₺`;
};

/**
 * Temizleme fonksiyonu: ₺ işaretini kaldırır
 */
export const cleanCurrency = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).replace(/₺/g, "").trim();
};

export const getDefaultGeneralValues = (product) => {
  const baseValues = {
    kagitBardak: "0.40",
    elemanMaliyet: "87000",
    elemanSayisi: "",
    sekerBirimFiyat: "0.00",
    makineOmru: "2",
  };

  const productSpecificValues = {
    cay: { cayKgFiyat: "300", caySatisFiyat: "40" },
    turkKahvesi: { turkKahveKgFiyat: "840", turkKahvesiSatisFiyat: "60" },
    filtreKahve: { filtreKahveKgFiyat: "1000", filtreKahveSatisFiyat: "60" },
    espresso: { espressoKgFiyat: "1200", espressoSatisFiyat: "90" },
  };

  return { ...baseValues, ...productSpecificValues[product] };
};

export const getDefaultMachineInputs = (product) => {
  const defaults = {
    cay: { cayKahveGr: "3.3", sekerGr: "0", kagitBardakKatsayi: "0", elemanIhtiyaci: "0", elemanVerimliligi: "5", kullaniciPersonelVerimliligi: "0", kayipKacakOrani: "10", servisBakimMaliyeti: "10" },
    turkKahvesi: { cayKahveGr: "10", sekerGr: "0", kagitBardakKatsayi: "0", elemanIhtiyaci: "0", elemanVerimliligi: "5", kullaniciPersonelVerimliligi: "0", kayipKacakOrani: "10", servisBakimMaliyeti: "10" },
    filtreKahve: { cayKahveGr: "12", sekerGr: "0", kagitBardakKatsayi: "0", elemanIhtiyaci: "0", elemanVerimliligi: "5", kullaniciPersonelVerimliligi: "0", kayipKacakOrani: "10", servisBakimMaliyeti: "10" },
    espresso: { cayKahveGr: "12", sekerGr: "0", kagitBardakKatsayi: "0", elemanIhtiyaci: "0", elemanVerimliligi: "5", kullaniciPersonelVerimliligi: "0", kayipKacakOrani: "10", servisBakimMaliyeti: "10" },
  };
  return defaults[product] || {};
};

export const calculateProductUnitPrice = (product, productState) => {
  let digerBirimFiyat =
    parseFloat(productState.birimMakineMaliyetiDiger || "0") +
    parseFloat(productState.cayKahveGrDiger || "0") +
    parseFloat(productState.sekerGrDiger || "0") +
    parseFloat(productState.kagitBardakKatsayiDiger || "0") +
    parseFloat(productState.elemanIhtiyaciDiger || "0") +
    parseFloat(productState.elemanVerimliligiDiger || "0") +
    parseFloat(productState.kullaniciPersonelVerimliligiDiger || "0") +
    parseFloat(productState.kayipKacakOraniDiger || "0") +
    parseFloat(productState.servisBakimMaliyetiDiger || "0");

  productState.digerBirimFiyat = roundToTwoDecimals(digerBirimFiyat).toFixed(2);
};

export const calculateProductCosts = (product, productState, generalParams, machineInputs, productStates, toplamAylikTuketim) => {
  const aylikTuketim = parseFloat(productState.aylikTuketim || "0") || 0;
  const yillikTuketim = parseFloat(productState.yillikTuketim || "0") || 0;

  if (aylikTuketim <= 0) return;

  let toplamTuketim = toplamAylikTuketim;
  if (toplamTuketim === undefined) {
    toplamTuketim = Object.values(productStates).reduce((sum, state) => {
      const tuketim = parseFloat(state?.aylikTuketim || "0") || 0;
      return sum + tuketim;
    }, 0);
  }
  if (toplamTuketim === 0) {
    toplamTuketim = aylikTuketim;
  }

  // Çay / Kahve gr. Maliyet
  const kgFiyatKeyMap = {
    cay: "cayKgFiyat",
    turkKahvesi: "turkKahveKgFiyat",
    filtreKahve: "filtreKahveKgFiyat",
    espresso: "espressoKgFiyat",
  };
  const kgFiyatKey = kgFiyatKeyMap[product];
  const kgFiyat = parseFloat(cleanCurrency(generalParams[kgFiyatKey] || "0")) || 0;
  const cayKahveGr = parseFloat(machineInputs.cayKahveGr || "0") || 0;
  const cayKahveGrMaliyet = (kgFiyat / 1000) * cayKahveGr;
  productState.cayKahveGrDiger = roundToTwoDecimals(cayKahveGrMaliyet).toFixed(2);

  // Şeker gr. Maliyet
  const sekerBirimFiyat = parseFloat(generalParams.sekerBirimFiyat || "0") || 0;
  const sekerGr = parseFloat(machineInputs.sekerGr || "0") || 0;
  const sekerGrMaliyet = (sekerBirimFiyat / 1000) * sekerGr;
  productState.sekerGrDiger = roundToTwoDecimals(sekerGrMaliyet).toFixed(2);

  // Kağıt Bardak ve Karıştırıcı Maliyet
  const kagitBardakAdet = parseFloat(machineInputs.kagitBardakKatsayi || "0") || 0;
  const kagitBardakBirimFiyat = parseFloat(generalParams.kagitBardak || "0") || 0;
  if (aylikTuketim > 0) {
    const kagitBardakMaliyet = (kagitBardakAdet * kagitBardakBirimFiyat) / aylikTuketim;
    productState.kagitBardakKatsayiDiger = roundToTwoDecimals(kagitBardakMaliyet).toFixed(2);
  } else {
    productState.kagitBardakKatsayiDiger = "0";
  }

  // Eleman İhtiyacı Maliyet
  const elemanIhtiyaci = parseFloat(machineInputs.elemanIhtiyaci || "0") || 0;
  const elemanMaliyet = parseFloat(generalParams.elemanMaliyet || "0") || 0;
  if (toplamTuketim > 0) {
    const elemanIhtiyaciMaliyet = (elemanIhtiyaci * elemanMaliyet) / toplamTuketim;
    productState.elemanIhtiyaciDiger = roundToTwoDecimals(elemanIhtiyaciMaliyet).toFixed(2);
  } else {
    productState.elemanIhtiyaciDiger = "0";
  }

  // Eleman Verimliliği Maliyet
  const elemanVerimliligiYuzde = parseFloat(machineInputs.elemanVerimliligi || "0") || 0;
  const elemanVerimliligi = elemanVerimliligiYuzde / 100;
  if (aylikTuketim > 0) {
    const elemanVerimliligiMaliyet = (elemanMaliyet * elemanVerimliligi) / aylikTuketim;
    productState.elemanVerimliligiDiger = roundToTwoDecimals(elemanVerimliligiMaliyet).toFixed(2);
  } else {
    productState.elemanVerimliligiDiger = "0";
  }

  // Kullanıcı Personel Verimliliği Maliyet
  const kullaniciPersonelVerimliligi = parseFloat(machineInputs.kullaniciPersonelVerimliligi || "0") || 0;
  const kullaniciPersonelVerimliligiMaliyet = (elemanMaliyet / 11400) * kullaniciPersonelVerimliligi;
  productState.kullaniciPersonelVerimliligiDiger = roundToTwoDecimals(kullaniciPersonelVerimliligiMaliyet).toFixed(2);

  // Kayıp Kaçak Oranı Maliyet
  const satisFiyatKeyMap = {
    cay: "caySatisFiyat",
    turkKahvesi: "turkKahvesiSatisFiyat",
    filtreKahve: "filtreKahveSatisFiyat",
    espresso: "espressoSatisFiyat",
  };
  const satisFiyatKey = satisFiyatKeyMap[product];
  const satisFiyat = parseFloat(generalParams[satisFiyatKey] || "0") || 0;
  const kayipKacakOraniYuzde = parseFloat(machineInputs.kayipKacakOrani || "0") || 0;
  const kayipKacakOrani = kayipKacakOraniYuzde / 100;
  const kayipKacakOraniMaliyet = satisFiyat * kayipKacakOrani;
  productState.kayipKacakOraniDiger = roundToTwoDecimals(kayipKacakOraniMaliyet).toFixed(2);

  // Diğer Makine Tutarı — adet × birim fiyat.
  // Tek kaynaktan türetilir; kullanıcı makine alanlarına dokunmadan sadece
  // tüketim girdiğinde de amortisman ve servis maliyeti hesaba katılır.
  const makineAdetDiger = parseFloat(productState.makineAdetDiger || "0") || 0;
  const digerMakineFiyat =
    parseFloat(cleanCurrency(String(productState.digerFiyat ?? "0"))) || 0;
  const makineTutari = makineAdetDiger * digerMakineFiyat;
  productState.makineTutariDiger = makineTutari.toFixed(0);

  // Birim Makine Maliyeti
  const makineOmru = parseFloat(generalParams.makineOmru || "2") || 1;
  if (yillikTuketim > 0 && makineOmru > 0 && makineTutari > 0) {
    const birimMakineMaliyeti = (makineTutari / yillikTuketim) / makineOmru;
    productState.birimMakineMaliyetiDiger = roundToTwoDecimals(birimMakineMaliyeti).toFixed(2);
  } else {
    productState.birimMakineMaliyetiDiger = "0";
  }

  // Servis Bakım Maliyeti
  const servisBakimYuzde = parseFloat(machineInputs.servisBakimMaliyeti || "10") || 0;
  const servisBakim = servisBakimYuzde / 100;
  if (yillikTuketim > 0) {
    const servisBakimMaliyeti = (makineTutari * servisBakim) / yillikTuketim;
    productState.servisBakimMaliyetiDiger = roundToTwoDecimals(servisBakimMaliyeti).toFixed(2);
  } else {
    productState.servisBakimMaliyetiDiger = "0";
  }

  // Birim fiyatı hesapla
  calculateProductUnitPrice(product, productState);
};

